export const USER_CENTER_ORIGIN = "https://my.bdfz.net";
export const SITE_KEY = "ly";

export interface BdfzUser {
  id?: number;
  slug?: string;
  display_name?: string;
  displayName?: string;
  avatar_url?: string;
  avatarUrl?: string;
}

export interface BdfzSession {
  authenticated: boolean;
  user: BdfzUser | null;
}

export interface SyncProgressPayload {
  siteKey: string;
  itemKey: string;
  itemTitle: string;
  itemGroup: string;
  itemType: string;
  state: string;
  completed: boolean;
  score: number;
  progressPercent: number;
  meta: Record<string, unknown>;
}

export interface SyncRecordPayload {
  siteKey: string;
  kind: "event" | "conversation" | "download";
  recordKey: string;
  title: string;
  summary: string;
  itemGroup: string;
  itemType: string;
  payload: Record<string, unknown>;
}

export interface UpdateMeResponse {
  ok?: boolean;
  user?: BdfzUser | null;
}

function identity() {
  return window.BdfzIdentity;
}

async function userCenterApi(path: string, method: string, body?: unknown, signal?: AbortSignal): Promise<unknown> {
  const headers: HeadersInit = {};
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const response = await fetch(`${USER_CENTER_ORIGIN}${path}`, {
    method,
    headers,
    body: payload,
    signal,
    credentials: "include",
    mode: "cors",
  });
  if (!response.ok) {
    throw new Error(`User center ${method} ${path} failed: ${response.status}`);
  }
  return response.headers.get("content-type")?.includes("json")
    ? response.json()
    : null;
}

export function loginUrl(returnTo = window.location.href): string {
  return identity()?.buildAuthUrl?.("login", returnTo)
    ?? `${USER_CENTER_ORIGIN}/?returnTo=${encodeURIComponent(returnTo)}`;
}

export async function waitForIdentity(timeoutMs = 2500): Promise<typeof window.BdfzIdentity | null> {
  const current = identity();
  if (current) return current;

  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    await new Promise((resolve) => window.setTimeout(resolve, 80));
    const next = identity();
    if (next) return next;
  }
  return null;
}

export async function getSession(): Promise<BdfzSession> {
  const sdk = await waitForIdentity();
  if (sdk?.getSession) {
    const session = await sdk.getSession();
    return {
      authenticated: !!session?.authenticated,
      user: session?.user ?? null,
    };
  }

  try {
    const response = await fetch(`${USER_CENTER_ORIGIN}/api/session`, {
      credentials: "include",
      mode: "cors",
    });
    if (!response.ok) return { authenticated: false, user: null };
    const session = (await response.json()) as BdfzSession;
    return {
      authenticated: !!session?.authenticated,
      user: session?.user ?? null,
    };
  } catch {
    return { authenticated: false, user: null };
  }
}

/** Verify the session without converting a network failure into a logout. */
export async function getSessionStrict(): Promise<BdfzSession> {
  const sdk = await waitForIdentity(500);
  if (sdk?.getSession) {
    const result = await sdk.getSession();
    if (typeof result?.authenticated !== "boolean") throw new Error("session_unconfirmed");
    return { authenticated: result.authenticated, user: result.user ?? null };
  }
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 10000);
  try {
    const result = await userCenterApi("/api/session", "GET", undefined, controller.signal) as BdfzSession;
    if (typeof result?.authenticated !== "boolean") throw new Error("session_unconfirmed");
    return result;
  } finally { window.clearTimeout(timer); }
}

export function readUserCenter(path: string): Promise<unknown> {
  if (!path.startsWith("/api/student-progress?")) throw new Error("unsupported_read");
  return userCenterApi(path, "GET");
}

function requireSyncReceipt(result: unknown): void {
  const receipt = result as { ok?: boolean; queued?: boolean } | undefined;
  if (!receipt || receipt.ok !== true || receipt.queued === true) throw new Error("sync_unconfirmed");
}

export async function logout(): Promise<void> {
  const sdk = await waitForIdentity(500);
  try {
    if (sdk?.api) {
      await sdk.api("/api/logout", { method: "POST" });
      return;
    }
    await fetch(`${USER_CENTER_ORIGIN}/api/logout`, {
      method: "POST",
      credentials: "include",
      mode: "cors",
    });
  } catch {
    /* logout is best-effort; the next session refresh is authoritative */
  }
}

export async function syncProgress(payload: SyncProgressPayload): Promise<void> {
  const sdk = await waitForIdentity(500);
  if (sdk?.syncProgress) {
    requireSyncReceipt(await sdk.syncProgress(payload));
    return;
  }
  await userCenterApi("/api/progress", "PUT", payload);
}

export async function recordEvent(payload: SyncRecordPayload): Promise<void> {
  const sdk = await waitForIdentity(500);
  if (sdk?.recordEvent) {
    requireSyncReceipt(await sdk.recordEvent(payload));
    return;
  }
  await userCenterApi("/api/data-records", "PUT", payload);
}

export async function updateUserAvatar(avatar: string): Promise<BdfzUser | null> {
  const value = avatar.trim();
  const payload = value.startsWith("data:image/")
    ? { avatarData: value }
    : { avatarUrl: value };
  const sdk = await waitForIdentity(500);
  const response = sdk?.api
    ? await sdk.api("/api/me", { method: "PATCH", body: payload })
    : await userCenterApi("/api/me", "PATCH", payload);
  return ((response as UpdateMeResponse)?.user ?? null) as BdfzUser | null;
}

export function displayName(user: BdfzUser | null | undefined): string {
  if (!user) return "";
  return user.display_name || user.displayName || user.slug || "Seiue";
}

export function avatarUrl(user: BdfzUser | null | undefined): string {
  return user?.avatar_url || user?.avatarUrl || "";
}
